/* eslint-disable arrow-body-style */
import React, { Fragment, useContext, useMemo } from 'react';

import { FormComponentProps } from 'antd/lib/form';
import { Checkbox, Radio } from 'bsa-ui';
import _, { isEmpty, isNil, keyBy, omit, snakeCase } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Paragraph, Select } from 'scouting-ui/lib';

import {
  BlueCardAdvancementNotStarted,
  BlueCardAdvancementStatus,
} from '@appTypes/enums';
import type {
  CounselorYouth,
  MeritBadgeCounselorProfile,
  PostUserMeritBadgeBlueCardsReq,
  UserMeritBadgesReq,
} from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import { useLazyPostBulkMbcMeritBadgeBlueCardsQuery } from '@modules/meritBadgeCounselors/api';
import {
  dashboardYouthsSel,
  mbcProfileCounselingMBsSel,
  mbcProfileSel,
  mbcYouthsByMBSel,
} from '@modules/meritBadgeCounselors/mbcDashboard/duck/selectors';
import { useGetYouthMeritBadgeDetailsQuery } from '@modules/rtk/esb-api';
import { Form, Img, Modal } from '@modules/shared/components';
import {
  advancementStatuses,
  advancementTypes,
} from '@modules/shared/constants';
import { organizationGuidSel } from '@modules/shared/duck/selectors';
import { intl } from '@modules/shared/localization';
import { useLazyPostMeritBadgeBlueCardsQuery } from '@modules/youthProfile/api';
import { mbStatusToBlueCardStatus } from '@modules/youthProfile/constants';
import { youthAdvRequirementsRequest } from '@modules/youthProfile/duck/actions';
import {
  approvedItemsSel,
  completedItemsSel,
  startedItemsSel,
  youthAdvRequirementsParamsSel,
  youthSel,
} from '@modules/youthProfile/duck/selectors';
import type { YouthGeneralMeritBadge } from '@modules/youthProfile/types';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';
import { profileSel } from '@user';

import { AdvRequirementsContext } from '../../context';
import PrintBlueCardButton from '../PrintBlueCardButton';
import styles from './PrintBlueCardModal.less';
import type { MbcMeritBadgeOption, YouthMeritBadgeOption } from './types';

const PAPER_TYPE = 'paperType';
const USE_SPECIFIC_PAPER_FIELD = 'useSpecificPaper';
const USE_SKU_66194 = 'useSKU66194';
const SHOW_UNIT_LEADER_SIGNATURE_FIELD = 'showUnitLeaderSignature';
const SHOW_MBC_SIGNATURE_FIELD = 'showMbcSignature';

type PrintBlueCardModalProps = {
  open: boolean;
  mode: 'single' | 'multiple' | 'mbc';
  mbcScoutOrgGuid?: string;
  onCancel: () => void;
} & FormComponentProps<{
  multipleMeritBadges?: number[];
  paperType: 'useSpecificPaper' | 'useSKU66194' | undefined;
  showUnitLeaderSignature: boolean;
  showMbcSignature: boolean;
}>;

const isLeaderSignedMb = (mb: YouthGeneralMeritBadge) =>
  !isNil(mb.leaderSignedDate) && !isEmpty(mb.leaderSignedDate);

const PrintBlueCardModal: React.FC<PrintBlueCardModalProps> = ({
  form,
  open,
  mode,
  mbcScoutOrgGuid,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const organizationGuid = useSelector(organizationGuidSel);
  const youth = useSelector(youthSel);
  const startedItems = useSelector(startedItemsSel);
  const completedItems = useSelector(completedItemsSel);
  const approvedItems = useSelector(approvedItemsSel);
  const mbcProfile = useSelector(mbcProfileSel);
  const mbcProfileCounselingMBs = useSelector(mbcProfileCounselingMBsSel);
  const youthGroupedByMB = useSelector(mbcYouthsByMBSel);
  const profile = useSelector(profileSel);
  const counseledYouths = useSelector(dashboardYouthsSel);
  const {
    userId,
    advancementId,
    userId: youthUserId,
  } = useSelector(youthAdvRequirementsParamsSel);

  const { meritBadgesById } = useMBDict({ params: { status: 'all' } });

  const counseledYouth = useMemo(
    () =>
      counseledYouths?.find(
        youth => youth.userId && youth.userId === +youthUserId,
      ),
    [counseledYouths, youthUserId],
  );

  const mbSelection = form.getFieldValue('multipleMeritBadges');
  const multipleOrMbcMode = ['multiple', 'mbc'].includes(mode);

  const isMbcMode = useMemo(() => mode === 'mbc', [mode]);

  // MBs that are selectable for MBC
  const mbcCounselingMbOptionsToMap = useMemo(() => {
    const filteredMbs = mbcProfileCounselingMBs
      .filter(mb => !isNil(youthGroupedByMB[mb.meritBadgeId]))
      .map(counselorMB => ({
        ...counselorMB,
        advancementType: advancementTypes.MERIT_BADGES,
        name: counselorMB.meritBadgeName,
        multipleStatuses: youthGroupedByMB[counselorMB.meritBadgeId]?.map(
          youthMb => _.replace(youthMb.mb.status ?? '', /\s/g, ''),
        ),
        status: '',
        imageUrl100:
          meritBadgesById.get(counselorMB.meritBadgeId)?.imageUrl100 ?? '',
      }));

    return filteredMbs.map(mb => {
      return { ...mb, id: mb.meritBadgeId };
    });
  }, [mbcProfileCounselingMBs, youthGroupedByMB, meritBadgesById]);

  // MBs that belong to a youth
  const mbOptionsToMap = useMemo(() => {
    const filteredMbs = [
      ...startedItems,
      ...approvedItems,
      ...completedItems,
    ].filter(
      adv => adv.advancementType === advancementTypes.MERIT_BADGES,
    ) as YouthGeneralMeritBadge[];

    return filteredMbs.map(mb => {
      if (mb.status === advancementStatuses.STARTED && isLeaderSignedMb(mb)) {
        return { ...mb, status: 'leaderSigned' };
      }
      return mb;
    });
  }, [startedItems, approvedItems, completedItems]);

  const { mbOptions, mbDict } = useMemo(() => {
    const mbDict = isMbcMode
      ? keyBy<MbcMeritBadgeOption>(mbcCounselingMbOptionsToMap, 'id')
      : keyBy<YouthMeritBadgeOption>(mbOptionsToMap, 'id');

    const allOption = {
      value: -1,
      label: (
        <span>
          <FormattedMessage id="bulkEntry.meritBadge.blueCard.allMeritBadges" />
        </span>
      ),
      name,
    };

    const mbOptionsMapping = isMbcMode
      ? (mbcCounselingMbOptionsToMap as MbcMeritBadgeOption[])
      : (mbOptionsToMap as YouthMeritBadgeOption[]);

    const mbOptions = _.chain<MbcMeritBadgeOption | YouthMeritBadgeOption>(
      mbOptionsMapping,
    )
      .sortBy('name')
      .map(({ id, name, imageUrl100 }) => ({
        value: id,
        label: (
          <span>
            <Img width="18px" className="mr-1" src={imageUrl100} title={name} />
            {name}
          </span>
        ),
        name,
      }))
      .value();
    return { mbOptions: [allOption, ...mbOptions], mbDict };
  }, [isMbcMode, mbOptionsToMap, mbcCounselingMbOptionsToMap]);

  const allMbselection = useMemo(() => {
    const mbOptionsMapping = isMbcMode
      ? (mbcCounselingMbOptionsToMap as MbcMeritBadgeOption[])
      : (mbOptionsToMap as YouthMeritBadgeOption[]);
    return _.chain<MbcMeritBadgeOption | YouthMeritBadgeOption>(
      mbOptionsMapping,
    )
      .map(mb => Number(mb.id))
      .value();
  }, [isMbcMode, mbOptionsToMap, mbcCounselingMbOptionsToMap]);

  const [fetchBlueCard, { isLoading: loadingBlueCard }] =
    useLazyPostMeritBadgeBlueCardsQuery({});

  const [fetchMbcBulkBlueCard, { isLoading: loadingMbcBulkBlueCard }] =
    useLazyPostBulkMbcMeritBadgeBlueCardsQuery({});

  const { isAdvancementInYouthRecord } = useContext(AdvRequirementsContext);
  const { data: meritBadgeDetails } = useGetYouthMeritBadgeDetailsQuery(
    {
      userId: userId,
      meritBadgeId: advancementId,
    },
    { skip: !open || !isAdvancementInYouthRecord || multipleOrMbcMode },
  );
  const { getFieldDecorator } = form;

  const sharedRules = [
    {
      required: true,
      message: intl.formatMessage({
        id: 'shared.form.error.isRequired',
      }),
    },
  ];

  const changeMbsSelection = (id: number) => {
    if (id === -1) {
      form.setFieldsValue({ multipleMeritBadges: allMbselection });
    }
  };
  const handleSubmit = () => {
    form.validateFields(async (err, formValues) => {
      const {
        multipleMeritBadges,
        paperType,
        showUnitLeaderSignature,
        showMbcSignature,
      } = formValues;

      let refetchMb = false;

      let commonPayload: Omit<
        PostUserMeritBadgeBlueCardsReq,
        'status' | 'users'
      > = {
        organizationGuid,
        isMBCSignatureDisplayed: showMbcSignature,
        isUnitLeaderSignatureDisplayed: showUnitLeaderSignature,
        isPlainPaperPrint: [USE_SPECIFIC_PAPER_FIELD, USE_SKU_66194].includes(
          paperType as string,
        ),
        isSKU66194: paperType === USE_SKU_66194,
      };

      let payload: PostUserMeritBadgeBlueCardsReq;

      if (multipleOrMbcMode) {
        const meritBadges =
          multipleMeritBadges?.map(meritBadgeId => ({ meritBadgeId })) ?? [];

        const statuses = isMbcMode
          ? [
              ...new Set(
                multipleMeritBadges?.flatMap(id => {
                  const mbDictTyped =
                    mbDict as _.Dictionary<MbcMeritBadgeOption>;
                  return mbDictTyped[id]?.multipleStatuses ?? [];
                }) ?? [],
              ),
            ]
          : multipleMeritBadges?.map(id => mbDict[id]?.status) ?? [];

        const users: UserMeritBadgesReq[] = isMbcMode
          ? Array.from(
              meritBadges.reduce((map, { meritBadgeId }) => {
                (youthGroupedByMB[meritBadgeId] || []).forEach(({ youth }) => {
                  if (youth.userId == null) return;

                  if (!map.has(youth.userId)) {
                    map.set(youth.userId, new Set());
                  }

                  map.get(youth.userId)!.add(meritBadgeId);
                });

                return map;
              }, new Map<number, Set<number>>()),
              ([userId, badges]) => ({
                userId,
                meritBadges: [...badges].map(meritBadgeId => ({
                  meritBadgeId,
                })),
              }),
            )
          : [
              {
                userId: userId ? Number(userId) : profile?.userId,
                meritBadges,
              },
            ];

        payload = {
          ...commonPayload,
          status: Array.from(new Set(statuses)).map(
            status =>
              mbStatusToBlueCardStatus[status] ??
              BlueCardAdvancementNotStarted.NOT_STARTED,
          ) as BlueCardAdvancementStatus[],
          users,
        };
      } else {
        let status = meritBadgeDetails?.status;

        if (isNil(status)) {
          status = BlueCardAdvancementNotStarted.NOT_STARTED;
          refetchMb = true;
        }

        let searchStatus = [status] as BlueCardAdvancementStatus[];

        if (status === BlueCardAdvancementStatus.LEADER_SIGNED) {
          searchStatus = [];
        }

        if (mbcScoutOrgGuid) {
          commonPayload = {
            ...commonPayload,
            organizationGuid: mbcScoutOrgGuid,
          };
        }

        payload = {
          ...commonPayload,
          status: searchStatus,
          users: [
            {
              userId: Number(userId),
              meritBadges: [
                {
                  meritBadgeId: Number(advancementId),
                },
              ],
            },
          ],
        };
      }

      try {
        const blueCardPdfData = isMbcMode
          ? await fetchMbcBulkBlueCard({
              payload: omit(payload, 'organizationGuid'),
            }).unwrap()
          : await fetchBlueCard({
              organizationGuid,
              payload,
            }).unwrap();

        if (refetchMb) {
          dispatch(youthAdvRequirementsRequest({ forceRefetch: true }));
        }

        const blob = new Blob([blueCardPdfData], {
          type: 'application/pdf',
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = isMbcMode
          ? `${snakeCase(
              getPreferredName(mbcProfile as MeritBadgeCounselorProfile),
            )}_mbc_bluecards.pdf`
          : `${snakeCase(
              youth.personShortFullName ||
                getPreferredName(counseledYouth as CounselorYouth),
            )}_${snakeCase(meritBadgeDetails?.name)}_bluecard.pdf`;

        link.click();

        URL.revokeObjectURL(url);
        onCancel();
      } catch {
        toastService.error(
          <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.errorPrintingBlueCard" />,
        );
      }
    });
  };

  return (
    <Modal
      className={styles.modal}
      title={
        <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.title" />
      }
      maskClosable={!loadingBlueCard && !loadingMbcBulkBlueCard}
      visible={open}
      onCancel={onCancel}
    >
      <p className={styles.description}>
        <FormattedMessage
          id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.description"
          values={{
            paperType: (
              <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.description.paperType">
                {(text: string) => (
                  <span className={styles.paperType}>{text}</span>
                )}
              </FormattedMessage>
            ),
          }}
        />
      </p>

      {multipleOrMbcMode && (
        <Fragment>
          <Paragraph font="body-caption">
            <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.badgeSelection" />
          </Paragraph>
          <Form.Item className={styles.formItem}>
            {getFieldDecorator('multipleMeritBadges', {
              rules: sharedRules,
              initialValue: [],
            })(
              <Select
                allowClear
                optionFilterProp="name"
                mode="multiple"
                options={mbOptions}
                onSelect={changeMbsSelection}
              />,
            )}
          </Form.Item>
        </Fragment>
      )}

      <Paragraph font="body-caption">
        <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.specifications" />
      </Paragraph>

      <Form.Item className={styles.formItem}>
        {getFieldDecorator(PAPER_TYPE, {
          initialValue: undefined,
        })(
          <Radio.Group>
            <Radio value={USE_SPECIFIC_PAPER_FIELD}>
              <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.usePaperFieldLabel" />
            </Radio>
            <Radio value={undefined}>
              <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.useSku649857" />
            </Radio>
            <Radio value={USE_SKU_66194}>
              <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.useSku66194" />
            </Radio>
          </Radio.Group>,
        )}
      </Form.Item>
      <Form.Item className={styles.formItem}>
        {getFieldDecorator(SHOW_UNIT_LEADER_SIGNATURE_FIELD, {
          initialValue: false,
          valuePropName: 'checked',
        })(
          <Checkbox>
            <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.showUnitLeaderSignatureFieldLabel" />
          </Checkbox>,
        )}
      </Form.Item>
      <Form.Item className={styles.formItem}>
        {getFieldDecorator(SHOW_MBC_SIGNATURE_FIELD, {
          initialValue: false,
          valuePropName: 'checked',
        })(
          <Checkbox>
            <FormattedMessage id="youthProfile.AdvRequirementsPage.PrintBlueCardModal.showMBCSignatureFieldLabel" />
          </Checkbox>,
        )}
      </Form.Item>
      <PrintBlueCardButton
        disabled={multipleOrMbcMode ? !mbSelection?.length : false}
        loading={loadingBlueCard || loadingMbcBulkBlueCard}
        isPrimary
        onClick={handleSubmit}
        className={styles.button}
      />
    </Modal>
  );
};

export default Form.create<PrintBlueCardModalProps>()(PrintBlueCardModal);
